"use client";

import React, { useState, useEffect, useRef } from "react";
import { Popover, PopoverPanel } from "@headlessui/react";
import { PlayIcon, StopIcon, Cog6ToothIcon } from "@heroicons/react/24/solid";
import { Rnd } from "react-rnd";
import { toast } from "sonner";
import userSettingStore from "@/lib/store/settingStore";
import { TimerIcon } from "lucide-react";

interface FocusTimerDialogProps {
  isOpen: boolean;
}

const FocusTimerDialog: React.FC<FocusTimerDialogProps> = ({ isOpen }) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null); // 타이머 참조
  const lastActivityTimeRef = useRef<number>(Date.now()); // ref로 관리
  const {
    goalTime,
    inactivityThreshold,
    mode,
    setGoalTime,
    setInactivityThreshold,
    setMode,
  } = userSettingStore();

  const [remainingTime, setRemainingTime] = useState<number>(0); // 남은 시간 (초)
  const [focusedTime, setFocusedTime] = useState<number>(0); // 작업 시간 (초)
  const [isRunning, setIsRunning] = useState<boolean>(false); // 타이머 실행 여부
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false); // 설정 UI 상태

  const formatTime = (seconds: number) => {
    const safeSeconds = Math.max(0, seconds); // 음수 방지
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!isOpen && isRunning) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null; // 타이머 초기화
      setIsRunning(false); // 타이머 상태 업데이트
    }
  }, [isOpen, isRunning]);

  const startTimer = () => {
    if (timerRef.current) return; // 중복 실행 방지

    setRemainingTime(goalTime * 60);
    setIsRunning(true);

    timerRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTimeRef.current;

      // 비활동 시간 확인
      if (timeSinceLastActivity <= inactivityThreshold * 1000) {
        setFocusedTime((prev) => prev + 1); // 작업 시간 증가
      }

      // 남은 시간 감소
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null; // 타이머 초기화
          setIsRunning(false);
          toast.info("목표 시간에 도달했습니다!");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 타이머 리셋
  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null; // 타이머 초기화
    setRemainingTime(goalTime * 60);
    setFocusedTime(0);
    setIsRunning(false);
  };

  // 사용자 활동 시 마지막 활동 시간 업데이트
  const handleActivity = () => {
    lastActivityTimeRef.current = Date.now();
  };

  useEffect(() => {
    if (isRunning) {
      const activityEvents = ["mousemove", "keydown", "click"];

      activityEvents.forEach(
        (event) =>
          window.addEventListener(event, handleActivity, { passive: true }), // passive 옵션 추가
      );

      return () => {
        activityEvents.forEach((event) =>
          window.removeEventListener(event, handleActivity),
        );
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [isRunning]);

  return (
    <>
      {isOpen && (
        <Rnd
          default={{
            x: 350,
            y: 20,
            width: "auto",
            height: "auto",
          }}
          bounds="window"
          enableResizing={false}
          disableDragging={isSettingsOpen} // react-rnd에서 지원하는 disableDragging 사용
          className="z-[50]"
        >
          <Popover className="relative">
            <PopoverPanel
              static
              className="absolute bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg w-40 px-4 py-1 z-[50] ring-1"
            >
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between">
                  <p className="text-sm">
                    {mode === "remaining" ? "남은 시간" : "작업 시간"}
                  </p>
                  <div className="flex gap-x-1">
                    <button
                      onClick={() => setIsSettingsOpen((prev) => !prev)}
                      className="text-gray-500 hover:text-black dark:hover:text-white"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>
                    {isRunning ? (
                      <button
                        onClick={resetTimer}
                        className="text-red-500 hover:text-red-700"
                      >
                        <StopIcon className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={startTimer}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-3xl font-bold">
                  {mode === "remaining"
                    ? formatTime(remainingTime)
                    : formatTime(focusedTime)}
                </p>
              </div>
            </PopoverPanel>
          </Popover>
          {isSettingsOpen && (
            <div className="absolute top-full mt-20 bg-white ring-1 ring-gray-200 dark:ring-gray-800 dark:bg-black rounded-lg shadow-lg w-72 p-4 z-[50]">
              <div className="flex items-center">
                <TimerIcon className="w-4 h-4" />
                <h2 className="text-lg font-semibold ml-2 text-black dark:text-white">
                  타이머 설정
                </h2>
              </div>
              <div className="mt-4">
                <label className="block text-sm">목표 시간 (분)</label>
                <input
                  type="number"
                  value={goalTime}
                  onChange={(e) => setGoalTime(Number(e.target.value))}
                  className="w-full p-2 mt-2 text-sm border rounded-lg focus:outline-none focus:ring focus:ring-blue-500"
                />
                <label className="block text-sm mt-4">
                  비활동 시간 임계값 (초)
                </label>
                <input
                  type="number"
                  value={inactivityThreshold}
                  onChange={(e) =>
                    setInactivityThreshold(Number(e.target.value))
                  }
                  className="w-full p-2 mt-2 text-sm border rounded-lg focus:outline-none focus:ring focus:ring-blue-500"
                />
                <label className="block text-sm mt-4">모드 선택</label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-full p-2 mt-2 text-sm border rounded-lg focus:outline-none focus:ring focus:ring-blue-500"
                >
                  <option value="remaining">남은 시간 (CountDown)</option>
                  <option value="focusedTime">작업 시간 (StopWatch)</option>
                </select>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="px-3 py-1 icon-button"
                >
                  저장
                </button>
              </div>
            </div>
          )}
        </Rnd>
      )}
    </>
  );
};

export default FocusTimerDialog;
